import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Leaf, MapPin, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { Reveal } from "@/components/Reveal";

const COLS = [
  { title: "Shop", type: "category", links: ["Skin Care", "Hair Care", "Body Care", "Face Care", "Lip", "Nails", "Eyes"] },
  { title: "Houses", type: "brand", links: ["Himalaya", "Lakmé", "Kama Ayurveda", "MAC", "Fenty Beauty", "The Ordinary"] },
];

const norm = (s) =>
  (s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]/g, "");

const DEFAULT_LEGAL = [
  { slug: "privacy", title: "Privacy Policy" },
  { slug: "terms", title: "Terms of Use" },
  { slug: "return-policy", title: "Return & Cancellation Policy" },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [legalPages, setLegalPages] = useState(DEFAULT_LEGAL);
  const { catalog } = useStore();

  const linkFor = (type, label) => {
    const n = norm(label);
    const list = type === "category" ? catalog.categories : catalog.brands;
    const hit = list.find((x) => {
      const xn = norm(x.name);
      return xn === n || (n.length >= 3 && xn.includes(n)) || (xn.length >= 4 && n.includes(xn));
    });
    if (hit) return type === "category" ? `/category/${hit.id}` : `/shop?brand_id=${hit.id}`;
    return `/shop?q=${encodeURIComponent(label)}`;
  };

  useEffect(() => {
    api.get("/ecom/pages")
      .then((r) => { if (r.data?.items?.length) setLegalPages(r.data.items); })
      .catch(() => {});
  }, []);

  const subscribe = () => {
    if (!email.includes("@")) {
      toast.error("Enter a valid email to join the sanctuary");
      return;
    }
    toast.success("Welcome to the sanctuary — SOONYOUNG10 is yours");
    setEmail("");
  };

  return (
    <footer id="contact" data-testid="site-footer" className="relative overflow-hidden border-t border-champagne/10 bg-forest">
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-14">
        <Reveal>
          <div className="grid gap-10 rounded-3xl border border-gold/15 bg-pine/50 p-8 sm:p-12 lg:grid-cols-2">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-gold">The Botanical Post</p>
              <h3 className="mt-3 font-display text-3xl leading-tight text-cream sm:text-4xl">
                Rituals, drops & <span className="italic text-gold">10% off</span> your first order
              </h3>
            </div>
            <div className="flex items-center">
              <div className="flex w-full gap-2">
                <input
                  data-testid="newsletter-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && subscribe()}
                  placeholder="your@email.com"
                  className="flex-1 rounded-full border border-champagne/15 bg-forest px-5 py-4 text-sm text-cream placeholder:text-fern focus:border-gold focus:outline-none"
                />
                <button
                  data-testid="newsletter-subscribe-button"
                  onClick={subscribe}
                  className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-full bg-gold text-forest transition-transform hover:scale-105"
                  aria-label="Subscribe"
                >
                  <ArrowRight className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </Reveal>

        <div className="mt-10 grid gap-10 sm:grid-cols-2 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <img src="/assets/soonyoung-logo-trim.png" alt="Soonyoung" className="h-9 w-auto mix-blend-multiply" />
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-sage">
              A multi-brand cosmetic showroom where Indian legacy and global icons
              share one blushing pink shelf.
            </p>
            <div className="mt-6 space-y-2 text-xs text-fern">
              <p className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-gold/60" /> Soonyoung Flagship, Mumbai · New Delhi</p>
              <p className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-gold/60" /> care@soonyoung.in</p>
              <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-gold/60" /> 1800-SOONYOUNG</p>
            </div>
          </div>
          {COLS.map((col) => (
            <div key={col.title}>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">{col.title}</p>
              <ul className="mt-4 space-y-2.5">
                {col.links.map((l) => (
                  <li key={l}>
                    <Link to={linkFor(col.type, l)} data-testid={`footer-link-${l.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`} className="text-sm text-sage transition-colors hover:text-gold">
                      {l}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Company</p>
            <ul className="mt-4 space-y-2.5" data-testid="footer-legal-links">
              <li>
                <Link data-testid="footer-link-branches" to="/stores" className="text-sm text-sage transition-colors hover:text-gold">
                  Our Branches
                </Link>
              </li>
              {legalPages.map((pg) => (
                <li key={pg.slug}>
                  <Link data-testid={`footer-legal-link-${pg.slug}`} to={`/${pg.slug}`} className="text-sm text-sage transition-colors hover:text-gold">
                    {pg.title}
                  </Link>
                </li>
              ))}
              <li>
                <Link data-testid="footer-legal-link-contact" to="/contact" className="text-sm text-sage transition-colors hover:text-gold">
                  Contact Us
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-champagne/10 pt-5">
          <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-fern">
            © 2026 Soonyoung Beauty Pvt. Ltd. — 100% Authentic
          </p>
          <p className="flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.25em] text-fern">
            Grown with <Leaf className="h-3 w-3 text-gold" /> in India
          </p>
        </div>
      </div>

      <div className="pointer-events-none select-none overflow-hidden" aria-hidden="true">
        <p className="-mb-6 bg-gradient-to-b from-gold/50 to-gold/5 bg-clip-text text-center font-display text-[17vw] font-bold leading-none text-transparent sm:-mb-10">
          SOONYOUNG
        </p>
      </div>
    </footer>
  );
}
